type LogLevel = 'info' | 'warn' | 'error' | 'security';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  event: string;
  details?: unknown;
  user?: string;
  errorCode?: string;
  traceId?: string;
}

/**
 * 構造化ロガー
 *
 * - 全環境: console 出力（ブラウザ DevTools / RUM ツールがキャプチャ）
 * - 本番環境: VITE_LOG_ENDPOINT が設定されていれば navigator.sendBeacon で送信
 */
/** `import.meta.env` の値は型上 `any` 相当なので、文字列だけを受け取る（AGENTS.md 1・2）。 */
const readEnvString = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined;

/**
 * ログ詳細から API エラーの `errorCode` / `traceId` を取り出す。
 *
 * `details` は `unknown`（呼び出し側は axios のエラーや任意の値を渡す）。
 * `as { response?: ... }` で黙らせると、形が変わったときに黙って `undefined` になるため
 * プロパティごとに実行時確認する。
 */
const readApiErrorContext = (details: unknown): { errorCode?: string; traceId?: string } => {
  if (typeof details !== 'object' || details === null || !('response' in details)) return {};
  const { response } = details;
  if (typeof response !== 'object' || response === null || !('data' in response)) return {};
  const { data } = response;
  if (typeof data !== 'object' || data === null) return {};
  const errorCode = 'errorCode' in data ? readEnvString(data.errorCode) : undefined;
  const traceId = 'traceId' in data ? readEnvString(data.traceId) : undefined;
  return { errorCode, traceId };
};

class Logger {
  private isDevelopment = import.meta.env.DEV;
  private logEndpoint = readEnvString(import.meta.env.VITE_LOG_ENDPOINT);

  private log(level: LogLevel, event: string, details?: unknown, user?: string) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      event,
      details,
      user,
    };

    // API エラーコンテキストを抽出
    const { errorCode, traceId } = readApiErrorContext(details);
    if (errorCode) entry.errorCode = errorCode;
    if (traceId) entry.traceId = traceId;

    // Console 出力（全環境）
    const consoleMethod = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;
    consoleMethod(`[${level.toUpperCase()}]`, event, details || '');

    // 本番ログ送信
    if (!this.isDevelopment && this.logEndpoint) {
      // 絞り込み済みの値をそのまま渡す。メソッド内で再判定すると到達不能な分岐が生まれる。
      this.sendToBackend(entry, this.logEndpoint);
    }
  }

  /** 送信先は呼び出し側で絞り込んだ値を受け取る（`this.logEndpoint!` を書かずに済む。AGENTS.md 3）。 */
  private sendToBackend(entry: LogEntry, endpoint: string) {
    try {
      const body = new Blob([JSON.stringify(entry)], { type: 'application/json' });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(endpoint, body);
      }
    } catch {
      // ログ送信失敗は無視（ログ送信のためにエラーを出さない）
    }
  }

  logLogin(user: string) {
    this.log('security', 'User login', { action: 'login' }, user);
  }

  logLogout(user: string) {
    this.log('security', 'User logout', { action: 'logout' }, user);
  }

  logPermissionDenied(user: string, resource: string) {
    this.log('security', 'Permission denied', { resource }, user);
  }

  logSessionTimeout() {
    this.log('security', 'Session timeout', { action: 'timeout' });
  }

  logError(message: string, error: unknown) {
    this.log('error', message, error);
  }

  logInfo(message: string, details?: unknown) {
    this.log('info', message, details);
  }

  logWarning(message: string, details?: unknown) {
    this.log('warn', message, details);
  }

  private _globalHandlersRegistered = false;

  /**
   * グローバルエラーハンドラーを登録
   * アプリ初期化時に1度呼び出す（HMR で複数回呼ばれても冪等）
   */
  setupGlobalHandlers() {
    if (this._globalHandlersRegistered) return;
    this._globalHandlersRegistered = true;

    window.addEventListener('error', (event) => {
      this.log('error', 'Uncaught error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.log('error', 'Unhandled promise rejection', {
        reason: event.reason instanceof Error ? event.reason.message : String(event.reason),
      });
    });
  }
}

export const logger = new Logger();
